import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  HelpCircle,
  Loader2,
  PanelRightClose,
  RefreshCw,
  Sparkles,
  Check,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useApiTokens } from '@/hooks/useApiTokens';
import type { ArticleFAQ } from '@/types/articles';
import { cn } from '@/lib/utils';

interface PAAQuestion {
  question: string;
  selected: boolean;
}

interface ArticleFAQPanelProps {
  focusKeyword: string;
  faqs: ArticleFAQ[];
  slug?: string;
  onUpdateFaqs: (faqs: ArticleFAQ[]) => void;
  onInsertFAQSchema: (faqs: ArticleFAQ[]) => void;
  onClose: () => void;
}

export function ArticleFAQPanel({
  focusKeyword,
  faqs,
  slug,
  onUpdateFaqs,
  onInsertFAQSchema,
  onClose,
}: ArticleFAQPanelProps) {
  const { currentBrandId } = useBrandManager();
  const { apiTokens } = useApiTokens(currentBrandId);
  const hasDataForSEO = !!(apiTokens?.dataforseo_login && apiTokens?.dataforseo_password);
  const [activeTab, setActiveTab] = useState<'fetch' | 'review'>('fetch');
  const [paaQuestions, setPaaQuestions] = useState<PAAQuestion[]>([]);
  const [isFetchingPAA, setIsFetchingPAA] = useState(false);
  const [isGeneratingAnswers, setIsGeneratingAnswers] = useState(false);
  const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null);

  const selectedCount = paaQuestions.filter((q) => q.selected).length;
  const validatedCount = faqs.filter((f) => f.isValidated).length;

  const handleFetchPAA = useCallback(async () => {
    if (!focusKeyword.trim()) {
      toast.error('Please set a focus keyword first');
      return;
    }

    if (!currentBrandId) {
      toast.error('No brand selected');
      return;
    }

    setIsFetchingPAA(true);
    try {
      const { data, error } = await supabase.functions.invoke('dataforseo-people-also-ask', {
        body: { 
          keyword: focusKeyword, 
          brandId: currentBrandId, 
          depth: 10, 
          people_also_ask_click_depth: 1 
        },
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      const questions = data?.questions || [];
      if (questions.length === 0) {
        toast.info(`No "People Also Ask" questions found for "${focusKeyword}"`);
      } else {
        setPaaQuestions(questions.map((q: string) => ({ question: q, selected: false })));
        toast.success(`Found ${questions.length} PAA questions`);
      }
    } catch (error: any) {
      console.error('Error fetching PAA:', error);
      toast.error(error.message || 'Failed to fetch PAA questions');
    } finally {
      setIsFetchingPAA(false);
    }
  }, [focusKeyword, currentBrandId]);

  const handleToggleQuestion = (index: number) => {
    setPaaQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, selected: !q.selected } : q))
    );
  };

  const handleSelectAll = () => {
    const allSelected = paaQuestions.every((q) => q.selected);
    setPaaQuestions((prev) => prev.map((q) => ({ ...q, selected: !allSelected })));
  };

  const handleGenerateAnswers = useCallback(async () => {
    const selectedQuestions = paaQuestions.filter((q) => q.selected);
    if (selectedQuestions.length === 0) {
      toast.error('Please select at least one question');
      return;
    }

    setIsGeneratingAnswers(true);
    const newFaqs: ArticleFAQ[] = [];

    try {
      // Generate a page URL for the FAQ context
      const pageUrl = slug 
        ? `${window.location.origin}/articles/${slug}`
        : `${window.location.origin}/article`;

      for (const q of selectedQuestions) {
        const { data, error } = await supabase.functions.invoke('generate-faq-answer', {
          body: { 
            question: q.question, 
            keyword: focusKeyword,
            pageUrl,
          },
        });

        if (error) {
          console.error('Error generating answer for:', q.question, error);
          continue;
        }

        const answer = data?.answer || '';
        newFaqs.push({
          id: crypto.randomUUID(),
          question: q.question,
          answer,
          isValidated: false,
        });
      }

      if (newFaqs.length > 0) {
        onUpdateFaqs([...faqs, ...newFaqs]);
        // Clear selected questions
        setPaaQuestions((prev) => prev.filter((q) => !q.selected));
        toast.success(`Generated ${newFaqs.length} FAQ answers`);
        setActiveTab('review');
      } else {
        toast.error('Failed to generate any answers');
      }
    } catch (error: any) {
      console.error('Error generating answers:', error);
      toast.error(error.message || 'Failed to generate answers');
    } finally {
      setIsGeneratingAnswers(false);
    }
  }, [paaQuestions, focusKeyword, currentBrandId, faqs, onUpdateFaqs]);

  const handleUpdateFaq = (id: string, updates: Partial<ArticleFAQ>) => {
    onUpdateFaqs(faqs.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const handleDeleteFaq = (id: string) => {
    onUpdateFaqs(faqs.filter((f) => f.id !== id));
  };

  const handleToggleValidated = (id: string) => {
    handleUpdateFaq(id, { isValidated: !faqs.find((f) => f.id === id)?.isValidated });
  };

  const handleInsertToArticle = () => {
    const validatedFaqs = faqs.filter((f) => f.isValidated);
    if (validatedFaqs.length === 0) {
      toast.error('Please validate at least one FAQ');
      return;
    }
    onInsertFAQSchema(validatedFaqs);
  };

  const handleAddManualFaq = () => {
    const newFaq: ArticleFAQ = {
      id: crypto.randomUUID(),
      question: '',
      answer: '',
      isValidated: false,
    };
    onUpdateFaqs([...faqs, newFaq]);
    setExpandedFaqId(newFaq.id);
    setActiveTab('review');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Focus Keyword Display */}
      <div className="p-3 border-b bg-background/50">
        <div className="text-xs text-muted-foreground mb-1">Focus Keyword</div>
        <div className="text-sm font-medium">
          {focusKeyword || <span className="text-muted-foreground italic">Not set</span>}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'fetch' | 'review')} className="flex-1 flex flex-col min-h-0">
        <TabsList className="w-full justify-start rounded-none border-b bg-background px-3 shrink-0">
          <TabsTrigger value="fetch" className="text-xs">
            Fetch PAA
          </TabsTrigger>
          <TabsTrigger value="review" className="text-xs">
            Review ({faqs.length})
          </TabsTrigger>
        </TabsList>

        {/* Fetch Tab */}
        <TabsContent value="fetch" className="flex-1 flex flex-col m-0 min-h-0">
          <div className="p-3 border-b bg-background shrink-0 space-y-2">
            {!hasDataForSEO && (
              <div className="flex items-start gap-2 p-2.5 rounded-md border border-destructive/50 bg-destructive/10 text-destructive text-xs">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">DataForSEO not configured</p>
                  <p className="mt-0.5 text-destructive/80">
                    PAA fetching requires DataForSEO credentials.{' '}
                    <a href="/seo-api-tokens" className="underline font-medium hover:text-destructive">
                      Configure in SEO API Tokens →
                    </a>
                  </p>
                </div>
              </div>
            )}
            <Button
              variant={hasDataForSEO ? "outline" : "destructive"}
              size="sm"
              className="w-full"
              onClick={handleFetchPAA}
              disabled={isFetchingPAA || !focusKeyword.trim() || !hasDataForSEO}
            >
              {isFetchingPAA ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Fetch PAA Questions
            </Button>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-3 space-y-2">
              {paaQuestions.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <HelpCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No questions fetched yet.</p>
                  <p className="text-xs mt-1">
                    Click "Fetch PAA Questions" to get<br />People Also Ask suggestions.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">
                      {selectedCount} of {paaQuestions.length} selected
                    </span>
                    <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={handleSelectAll}>
                      {paaQuestions.every((q) => q.selected) ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  {paaQuestions.map((q, index) => (
                    <div
                      key={index}
                      className={cn(
                        'flex items-start gap-2 p-2 rounded-md border cursor-pointer transition-colors',
                        q.selected ? 'bg-primary/5 border-primary/30' : 'bg-background hover:bg-accent/50'
                      )}
                      onClick={() => handleToggleQuestion(index)}
                    >
                      <Checkbox checked={q.selected} className="mt-0.5" />
                      <span className="text-sm flex-1">{q.question}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </ScrollArea>

          {selectedCount > 0 && (
            <div className="p-3 border-t bg-background shrink-0">
              <Button
                size="sm"
                className="w-full"
                onClick={handleGenerateAnswers}
                disabled={isGeneratingAnswers}
              >
                {isGeneratingAnswers ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Generate {selectedCount} Answer{selectedCount > 1 ? 's' : ''}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Review Tab */}
        <TabsContent value="review" className="flex-1 flex flex-col m-0 min-h-0">
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-3">
              {faqs.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <HelpCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No FAQs generated yet.</p>
                  <p className="text-xs mt-1">
                    Fetch PAA questions and generate<br />answers to get started.
                  </p>
                </div>
              ) : (
                faqs.map((faq) => (
                  <div
                    key={faq.id}
                    className={cn(
                      'border rounded-lg bg-background overflow-hidden',
                      faq.isValidated && 'border-green-500/50'
                    )}
                  >
                    {/* FAQ Header */}
                    <div
                      className="flex items-center gap-2 p-2 cursor-pointer hover:bg-accent/50"
                      onClick={() => setExpandedFaqId(expandedFaqId === faq.id ? null : faq.id)}
                    >
                      {expandedFaqId === faq.id ? (
                        <ChevronUp className="h-4 w-4 shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 shrink-0" />
                      )}
                      <span className="text-sm flex-1 font-medium truncate">
                        {faq.question || 'New Question'}
                      </span>
                      {faq.isValidated && (
                        <Check className="h-4 w-4 text-green-600 shrink-0" />
                      )}
                    </div>

                    {/* Expanded Content */}
                    {expandedFaqId === faq.id && (
                      <div className="p-3 border-t space-y-3">
                        <div>
                          <Label className="text-xs">Question</Label>
                          <Textarea
                            value={faq.question}
                            onChange={(e) => handleUpdateFaq(faq.id, { question: e.target.value })}
                            placeholder="Enter question..."
                            className="mt-1 min-h-[60px] text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Answer</Label>
                          <Textarea
                            value={faq.answer}
                            onChange={(e) => handleUpdateFaq(faq.id, { answer: e.target.value })}
                            placeholder="Enter answer..."
                            className="mt-1 min-h-[100px] text-sm"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Switch
                              id={`validate-${faq.id}`}
                              checked={faq.isValidated}
                              onCheckedChange={() => handleToggleValidated(faq.id)}
                            />
                            <Label htmlFor={`validate-${faq.id}`} className="text-xs cursor-pointer">
                              Validated
                            </Label>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteFaq(faq.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {validatedCount > 0 && (
            <div className="p-3 border-t bg-background shrink-0">
              <Button size="sm" className="w-full" onClick={handleInsertToArticle}>
                <Plus className="h-4 w-4 mr-2" />
                Insert {validatedCount} FAQ{validatedCount > 1 ? 's' : ''} to Article
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
